import FormControl from "common/components/FormControl/FormControl";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isPasswordCorrect } from "common/components/Password/Password";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import type { User } from "types";
import { checkEmail } from "common/utils/fieldsValidators";
import CrudForm from "common/components/Crud/CrudForm";
import useAuth from "hooks/useAuth";
import fetchJSON from "common/utils/fetchJSON";

type Props = {
  onSubmit: (values: any) => any;
  onCancel?: () => void;
  item?: User;
  myAccount?: boolean;
};

function UserForm({ onSubmit, onCancel, item, myAccount }: Props) {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [changePassword, setChangePassword] = useState(false);
  const [roleOptions, setRoleOptions] = useState<{ value: number; label: string }[]>([]);
  const formApiRef = useRef<any>(null);

  const isSuperadmin = currentUser?.isSuperadmin;

  // Fetch available roles (Monitoring + SuperAdmin)
  useEffect(() => {
    if (!isSuperadmin) return;
    fetchJSON({ url: "users-permissions/roles", method: "GET" })
      .then((data) => {
        const filtered = (data.roles ?? [])
          .filter((r: any) => r.name === "Monitoring" || r.name === "SuperAdmin")
          .map((r: any) => ({ value: r.id, label: r.name }));
        setRoleOptions(filtered);
      })
      .catch(() => {});
  }, [isSuperadmin]);

  // Fetch user's current role separately (not included in the list API response)
  // and inject it into the form via form.change once loaded
  useEffect(() => {
    if (!isSuperadmin || !item?.id) return;
    fetchJSON({ url: `users/${item.id}?populate=role`, method: "GET" })
      .then((data) => {
        const roleId =
          data.role?.id ?? (typeof data.role === "number" ? data.role : undefined);
        if (roleId !== undefined && formApiRef.current) {
          formApiRef.current.change("role", roleId);
        }
      })
      .catch(() => {});
  }, [isSuperadmin, item?.id]);

  const initialValues = useMemo(
    () =>
      item?.id
        ? { ...item }
        : {
            name: undefined,
            password: undefined,
            email: "",
          },
    [item]
  );

  const checkPassword = (value: string) =>
    isPasswordCorrect(value) ? undefined : t("common.passwordTooWeak");

  const handleSubmit = (payload: any) => {
    const data = payload?.data ?? payload;
    const { confirmPassword, ...rest } = data;

    // L'API users-permissions attend un body plat (pas { data: {...} })
    const userData = rest.id
      ? rest
      : { ...rest, role: rest.role ?? 1, confirmed: true };

    // CrudEdit vérifie newItem.data.id pour distinguer création/mise à jour
    return onSubmit({ ...userData, data: userData });
  };

  return (
    <CrudForm
      onSubmit={handleSubmit}
      onCancel={onCancel}
      initialValues={initialValues}
      headerIcon={myAccount ? "UserCircleIcon" : "UsersIcon"}
      i18nNamespace="users"
      render={(_values, form) => {
        formApiRef.current = form;
        return (
          <div className="responsive-flex gap-2 mt-2">
            <div className="md:w-2/3 flex flex-col gap-2">
              <div className="white-box">
                <div className="responsive-flex gap-4">
                  <div className="flex flex-col w-full gap-4">
                    <FormControl
                      name="username"
                      title={t("users.username")}
                      required
                    />

                    {item?.id ? (
                      <>
                        <ToggleSwitch
                          rightLabel={t("users.newPassword")}
                          value={changePassword}
                          onChange={setChangePassword}
                        />
                        {changePassword && (
                          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                            <FormControl
                              type="jpassword"
                              name="password"
                              title={t("users.password")}
                              helpText={t("users.passwordInstruction")}
                              required
                              validate={checkPassword}
                            />
                            <FormControl
                              name="confirmPassword"
                              title={t("users.confirmPassword")}
                              type="password"
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <FormControl
                          type="jpassword"
                          name="password"
                          title={t("users.password")}
                          helpText={t("users.passwordInstruction")}
                          required
                          validate={checkPassword}
                        />
                        <FormControl
                          name="confirmPassword"
                          title={t("users.confirmPassword")}
                          type="password"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full md:w-1/3">
              <div className="white-box">
                <div className="w-full gap-2 flex flex-col">
                  <FormControl
                    name="email"
                    title={t("users.email")}
                    required
                    validate={(val: string) => checkEmail(val, t)}
                  />
                  {isSuperadmin && (
                    <FormControl
                      name="role"
                      title={t("users.role")}
                      type="select"
                      options={roleOptions}
                      required
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}
export default UserForm;
